import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  HStack,
  Text,
  Badge,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { fetchSummary, fetchList, resolveItems } from "../../../components/newData/useNewData";
import NewDataStepper from "../../../components/newData/NewDataStepper";
import NewDataHistory from "../../../components/newData/NewDataHistory";
import UnmatchedHolds from "../../../components/newData/UnmatchedHolds";

// Imports -> "New Data" tab panel (masters only — gated by Imports.js, no own H1).
// Pending queue + inline stepper + month history. See docs/michael/TASK-new-data-tab.md §7 S18.

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const toItems = (res) => {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.items)) return res.items;
  if (res && Array.isArray(res.data)) return res.data;
  if (res && Array.isArray(res.rows)) return res.rows;
  return [];
};

const displayCode = (code) => {
  const c = code == null ? "" : String(code);
  return c.startsWith("DESC:") ? "(no code)" : c;
};

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "");

const NewData = ({ refreshKey = 0 }) => {
  const toast = useToast();
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [stepperItems, setStepperItems] = useState(null); // {store:[], article:[]} snapshot while working
  const [loading, setLoading] = useState(true);
  const [ignoring, setIgnoring] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([fetchSummary(), fetchList({ status: "pending" })]);
      setSummary(s || null);
      setPending(toItems(list));
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not load the New Data queue",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    if (refreshKey > 0) setHistoryKey((k) => k + 1); // an upload just happened — refresh history too
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshKey]);

  const refreshAll = useCallback(() => {
    setHistoryKey((k) => k + 1);
    load();
  }, [load]);

  const storeItems = pending.filter((it) => it.kind === "store");
  const articleItems = pending.filter((it) => it.kind === "article");

  const openStepper = () => {
    setStepperItems({ store: storeItems, article: articleItems });
  };

  const closeStepper = () => {
    setStepperItems(null);
    refreshAll();
  };

  // Stepper reports each resolved item — drop it from the pending table/counts live.
  const handleResolved = (resolved) => {
    const id = resolved && typeof resolved === "object" ? resolved.id : resolved;
    if (id == null) return;
    setPending((prev) => prev.filter((it) => it.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allSelected = pending.length > 0 && selectedIds.length === pending.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : pending.map((it) => it.id));
  };

  const ignoreSelected = async () => {
    if (selectedIds.length === 0) return;
    setIgnoring(true);
    try {
      await resolveItems(selectedIds, "ignore");
      toast({
        title: "Ignored",
        description: `${selectedIds.length} item(s) marked as ignored`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setSelectedIds([]);
      refreshAll();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message || "Could not ignore the selected items",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIgnoring(false);
    }
  };

  const regions = (summary && summary.regions) || [];
  const suppliers = (summary && summary.suppliers) || [];

  return (
    <Box>
      {summary && summary.tableReady === false && (
        <Text fontSize="sm" color="orange.500" mb={3}>
          The New Data queue table has not been created on this environment yet.
        </Text>
      )}
      {summary && (
        <Text fontSize="sm" color="gray.500" mb={3}>
          Detection {summary.enabled ? "on" : "off"}
          {" · "}
          digest {summary.digestConfigured ? `to ${summary.digestTo}` : "not configured"}
          {summary.lastDetectionAt ? ` · last detection ${fmtDate(summary.lastDetectionAt)}` : ""}
          {summary.lastDigestAt ? ` · last digest ${fmtDate(summary.lastDigestAt)}` : ""}
        </Text>
      )}

      <HStack spacing={6} mb={4} flexWrap="wrap">
        <HStack>
          <Text fontWeight="semibold">Stores pending</Text>
          <Badge borderRadius="full" colorScheme={storeItems.length > 0 ? "orange" : "green"}>
            {storeItems.length}
          </Badge>
        </HStack>
        <HStack>
          <Text fontWeight="semibold">Articles pending</Text>
          <Badge borderRadius="full" colorScheme={articleItems.length > 0 ? "orange" : "green"}>
            {articleItems.length}
          </Badge>
        </HStack>
        {stepperItems ? (
          <Button size="sm" variant="outline" onClick={closeStepper}>
            Close stepper
          </Button>
        ) : (
          <Button size="sm" colorScheme="blue" onClick={openStepper} isDisabled={pending.length === 0}>
            Work through pending
          </Button>
        )}
      </HStack>

      {stepperItems && (
        <Box mb={6} p={4} borderWidth="1px" borderRadius="lg">
          <Tabs variant="line" size="sm">
            <TabList>
              <Tab>
                Stores
                <Badge ml={2} borderRadius="full">
                  {stepperItems.store.length}
                </Badge>
              </Tab>
              <Tab>
                Articles
                <Badge ml={2} borderRadius="full">
                  {stepperItems.article.length}
                </Badge>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <NewDataStepper
                  items={stepperItems.store}
                  kind="store"
                  regions={regions}
                  suppliers={suppliers}
                  onResolved={handleResolved}
                  onDone={closeStepper}
                />
              </TabPanel>
              <TabPanel px={0}>
                <NewDataStepper
                  items={stepperItems.article}
                  kind="article"
                  regions={regions}
                  suppliers={suppliers}
                  onResolved={handleResolved}
                  onDone={closeStepper}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}

      <HStack mb={2} justify="space-between">
        <Text fontWeight="semibold">Pending</Text>
        <Button
          size="sm"
          colorScheme="red"
          variant="outline"
          onClick={ignoreSelected}
          isLoading={ignoring}
          isDisabled={selectedIds.length === 0}
        >
          Ignore selected ({selectedIds.length})
        </Button>
      </HStack>

      {loading ? (
        <HStack py={6} justify="center">
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500">
            Loading…
          </Text>
        </HStack>
      ) : pending.length === 0 ? (
        <Text fontSize="sm" color="gray.500" py={4}>
          No pending items — all caught up.
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th px={2}>
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={selectedIds.length > 0 && !allSelected}
                    onChange={toggleSelectAll}
                  />
                </Th>
                <Th>Kind</Th>
                <Th>Retailer</Th>
                <Th>Code</Th>
                <Th>Description</Th>
                <Th>Supplier</Th>
                <Th>File</Th>
                <Th>Type</Th>
                <Th isNumeric>Seen</Th>
                <Th>Last seen</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pending.map((it) => (
                <Tr key={it.id}>
                  <Td px={2}>
                    <Checkbox isChecked={selectedIds.includes(it.id)} onChange={() => toggleSelected(it.id)} />
                  </Td>
                  <Td>
                    <Badge colorScheme={it.kind === "store" ? "purple" : "cyan"}>{it.kind}</Badge>
                  </Td>
                  <Td>{it.retailer}</Td>
                  <Td>{displayCode(it.code)}</Td>
                  <Td>{it.description}</Td>
                  <Td>{it.suppliercode}</Td>
                  <Td>{it.source_file}</Td>
                  <Td>{it.data_type}</Td>
                  <Td isNumeric>{it.seen_count}</Td>
                  <Td>{fmtDate(it.last_seen_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Divider my={6} />

      {/* PnP v2 unmatched rows — held because vendor AND article were unknown */}
      <HStack mb={2}>
        <Text fontWeight="semibold">Unmatched imports</Text>
        {summary?.holds?.rows > 0 && (
          <Badge borderRadius="full" colorScheme="orange">
            {summary.holds.rows} row(s)
          </Badge>
        )}
      </HStack>
      <UnmatchedHolds key={`holds-${historyKey}-${refreshKey}`} suppliers={suppliers} onChanged={refreshAll} />

      <Divider my={6} />

      <NewDataHistory key={historyKey} month={currentMonth()} />
    </Box>
  );
};

export default NewData;
